import { System } from '@ecs/System';
import { Position } from '@components/Position';
import { StandardWeapon } from '@components/StandardWeapon';
import { Energy } from '@components/Energy';
import { PlayerInput } from '@components/PlayerInput';
import { Tag } from '@components/Tag';
import { AI } from '@components/AI';
import { WeaponUtils } from '@utils/WeaponUtils';
import { SoundManager, SoundType } from '@audio/SoundManager';

export class StandardWeaponSystem extends System {
  private soundManager = SoundManager.getInstance();
  private canvas!: HTMLCanvasElement;

  setCanvas(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
  }
  update(_deltaTime: number): void {
    const currentTime = Date.now();
    
    // Handle player standard weapons
    this.handlePlayerWeapons(currentTime);
    
    // Handle enemy standard weapons
    this.handleEnemyWeapons(currentTime);
  }

  private handlePlayerWeapons(currentTime: number): void {
    const players = this.getEntitiesWithComponents(Position, StandardWeapon, Energy, PlayerInput, Tag);

    for (const player of players) {
      const tag = player.getComponent(Tag)!;
      if (tag.value !== 'player') continue;

      const position = player.getComponent(Position)!;
      const weapon = player.getComponent(StandardWeapon)!;
      const energy = player.getComponent(Energy)!;
      const input = player.getComponent(PlayerInput)!;

      // Check if weapon can shoot
      if (currentTime - weapon.lastShot < weapon.cooldown || energy.current < weapon.energyCost) {
        continue;
      }

      // An ordered focus target always takes priority while it is in range
      const focus = this.getFocusTarget(position, input, weapon.range);
      let target: Position | null = focus;

      if (!target) {
        switch (weapon.mode) {
          case 'auto':
            target = WeaponUtils.findNearestTarget(this.world, position, weapon.range, 'enemy')?.position ?? null;
            break;
          case 'manual':
            break; // only fires at ordered targets
          case 'defensive':
            target = this.findThreateningEnemy(position, currentTime, weapon.range);
            break;
        }
      }

      if (target && energy.current >= weapon.energyCost) {
        // Use energy once per volley
        energy.current -= weapon.energyCost;
        
        const aimAngle = Math.atan2(target.y - position.y, target.x - position.x);
        for (const angle of WeaponUtils.spreadAngles(aimAngle, weapon.projectileCount, weapon.spreadAngle)) {
          WeaponUtils.createBulletAtAngle(
            this.world,
            position,
            angle,
            weapon.bulletSpeed,
            weapon.bulletColor,
            true,
            weapon.damage
          );
        }
        
        weapon.lastShot = currentTime;
        
        // Play weapon sound
        this.soundManager.playSound(SoundType.STANDARD_WEAPON, 0.5);
        
        // Create muzzle flash
        WeaponUtils.createMuzzleFlash(
          this.world, 
          position, 
          Math.atan2(target.y - position.y, target.x - position.x),
          weapon.bulletColor
        );
      }
    }
  }

  private handleEnemyWeapons(currentTime: number): void {
    const enemies = this.getEntitiesWithComponents(Position, StandardWeapon, AI, Tag);
    const players = this.getEntitiesWithComponents(Position, Tag).filter(
      e => e.getComponent(Tag)!.value === 'player'
    );

    if (players.length === 0) return;

    for (const enemy of enemies) {
      const tag = enemy.getComponent(Tag)!;
      if (tag.value !== 'enemy') continue;

      const position = enemy.getComponent(Position)!;
      const weapon = enemy.getComponent(StandardWeapon)!;
      const ai = enemy.getComponent(AI)!;

      // Find nearest player
      let nearestPlayer = null;
      let minDistance = weapon.range;

      for (const player of players) {
        const playerPos = player.getComponent(Position)!;
        const distance = WeaponUtils.getDistance(position, playerPos);
        if (distance < minDistance) {
          minDistance = distance;
          nearestPlayer = playerPos;
        }
      }

      if (nearestPlayer && currentTime - ai.lastShot > weapon.cooldown) {
        // Only shoot if enemy is within viewport
        if (WeaponUtils.isInViewport(position, this.canvas)) {
          const aimAngle = Math.atan2(nearestPlayer.y - position.y, nearestPlayer.x - position.x);
          for (const angle of WeaponUtils.spreadAngles(aimAngle, weapon.projectileCount, weapon.spreadAngle)) {
            WeaponUtils.createBulletAtAngle(
              this.world,
              position,
              angle,
              weapon.bulletSpeed,
              weapon.bulletColor,
              false,
              weapon.damage
            );
          }
          WeaponUtils.createMuzzleFlash(this.world, position, aimAngle, weapon.bulletColor);
          ai.lastShot = currentTime;
          
          // Play weapon sound (quieter for enemies)
          this.soundManager.playSound(SoundType.STANDARD_WEAPON, 0.3);
        }
      }
    }
  }

  private getFocusTarget(position: Position, input: PlayerInput, range: number): Position | null {
    if (input.targetEntity === null) return null;
    const targetPos = this.world.getEntity(input.targetEntity)?.getComponent(Position);
    if (!targetPos || WeaponUtils.getDistance(position, targetPos) > range) return null;
    return targetPos;
  }

  private findThreateningEnemy(position: Position, currentTime: number, range: number): Position | null {
    const enemies = this.getEntitiesWithComponents(Position, AI, Tag);
    const threats = [];

    for (const enemy of enemies) {
      const tag = enemy.getComponent(Tag)!;
      if (tag.value !== 'enemy') continue;

      const enemyPos = enemy.getComponent(Position)!;
      const ai = enemy.getComponent(AI)!;
      const distance = WeaponUtils.getDistance(position, enemyPos);

      // Consider enemy threatening if close or recently shot - but never shoot beyond weapon range
      if (distance > range) continue;
      if (distance < range * 0.5 || currentTime - ai.lastShot < 1000) {
        threats.push({ position: enemyPos, distance });
      }
    }

    if (threats.length > 0) {
      threats.sort((a, b) => a.distance - b.distance);
      return threats[0].position;
    }

    return null;
  }
}